import { useState } from "react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import type { BodyMarker, BodyRegion } from "@/types/vision360";
import bodyMapMale from "@/assets/body-map-male.png";
import bodyMapFemale from "@/assets/body-map-female.png";

interface Vision360BodyMap2DProps {
  markers: BodyMarker[];
  sex?: "M" | "F" | string | null;
  onMarkerClick?: (marker: BodyMarker) => void;
}

// Coordenadas ANATÔMICAS para MASCULINO
// Convenção: esquerda do paciente = DIREITA na tela (vista frontal espelhada)
// ViewBox: 0 0 100 240 (imagem 800x1920)
// Coordenadas calibradas manualmente
const REGION_COORDS_MALE: Record<BodyRegion, { x: number; y: number }> = {
  // Cabeça e Pescoço
  cabeca: { x: 55.5, y: 14.2 },
  face: { x: 56.2, y: 27.4 },
  olhos: { x: 48.3, y: 19.9 },
  ouvidos: { x: 63.9, y: 23.2 },
  boca: { x: 51.2, y: 32.4 },
  pescoco: { x: 44.8, y: 37.4 },
  cervical: { x: 52.6, y: 43.8 },

  // Ombros (direito paciente = esquerda tela)
  ombro_direito: { x: 24.6, y: 51.8 },
  ombro_esquerdo: { x: 71.8, y: 50.4 },
  
  // Tronco
  torax: { x: 54.8, y: 67.7 },
  costas_superior: { x: 31.9, y: 44.9 },
  
  // Braços (direito paciente = esquerda tela)
  braco_direito: { x: 21.8, y: 67.9 },
  braco_esquerdo: { x: 77.1, y: 66.1 },
  cotovelo_direito: { x: 18.9, y: 85.4 },
  cotovelo_esquerdo: { x: 80.7, y: 85 },
  antebraco_direito: { x: 16.8, y: 98.9 },
  antebraco_esquerdo: { x: 81.1, y: 106.8 },
  punho_direito: { x: 12.5, y: 113.9 },
  punho_esquerdo: { x: 83.9, y: 115 },
  
  // Mãos
  mao_direita: { x: 12.1, y: 124.6 },
  mao_esquerda: { x: 85.7, y: 122.5 },

  // Tronco Médio e Inferior
  toracica: { x: 54.4, y: 67.4 },
  abdominal: { x: 50.5, y: 88.5 },
  lombar: { x: 51.5, y: 78.1 },
  sacral: { x: 56.5, y: 99.5 },
  quadril_direito: { x: 35, y: 105.7 },
  quadril_esquerdo: { x: 65, y: 107.9 },

  // Membros Inferiores (direito paciente = esquerda tela)
  coxa_direita: { x: 36.8, y: 154.6 },
  coxa_esquerda: { x: 62.9, y: 152.9 },
  joelho_direito: { x: 37.5, y: 169.6 },
  joelho_esquerdo: { x: 61.4, y: 170 },
  perna_direita: { x: 36.8, y: 149.3 },
  perna_esquerda: { x: 62.1, y: 149.3 },
  tornozelo_direito: { x: 42.1, y: 218.6 },
  tornozelo_esquerdo: { x: 57.9, y: 219.3 },
  pe_direito: { x: 36.8, y: 226.4 },
  pe_esquerdo: { x: 63.9, y: 228.6 },

  // Sistêmicos (fora do corpo - laterais)
  mental: { x: 46.2, y: 10.6 },
  metabolico: { x: 95, y: 50 },
  // Condições sistêmicas também precisam ficar visíveis no mapa
  sistemico: { x: 5, y: 42 },
  sono: { x: 5, y: 14 },
};

// Coordenadas ANATÔMICAS para FEMININO
// Ajustadas para silhueta feminina (ombros mais estreitos, quadril mais largo)
const REGION_COORDS_FEMALE: Record<BodyRegion, { x: number; y: number }> = {
  // Cabeça e Pescoço
  cabeca: { x: 50, y: 8 },
  face: { x: 50, y: 11 },
  olhos: { x: 50, y: 9 },
  ouvidos: { x: 57, y: 10 },
  boca: { x: 50, y: 13 },
  pescoco: { x: 50, y: 17 },
  cervical: { x: 50, y: 19 },

  // Ombros (mais estreitos que masculino)
  ombro_direito: { x: 37, y: 25 },
  ombro_esquerdo: { x: 63, y: 25 },
  
  // Tronco
  torax: { x: 50, y: 34 },
  costas_superior: { x: 50, y: 30 },
  
  // Braços (mais próximos ao corpo)
  braco_direito: { x: 30, y: 38 },
  braco_esquerdo: { x: 70, y: 38 },
  cotovelo_direito: { x: 24, y: 48 },
  cotovelo_esquerdo: { x: 76, y: 48 },
  antebraco_direito: { x: 20, y: 56 },
  antebraco_esquerdo: { x: 80, y: 56 },
  punho_direito: { x: 16, y: 64 },
  punho_esquerdo: { x: 84, y: 64 },
  
  // MÃOS - Posição corrigida (mais próximas do corpo)
  mao_direita: { x: 24, y: 68 },
  mao_esquerda: { x: 76, y: 68 },

  // Tronco Médio e Inferior
  toracica: { x: 50, y: 40 },
  abdominal: { x: 50, y: 56 },     // UMBIGO - posição corrigida (mais baixo)
  lombar: { x: 50, y: 58 },
  sacral: { x: 50, y: 66 },
  quadril_direito: { x: 40, y: 70 },   // Quadril mais largo
  quadril_esquerdo: { x: 60, y: 70 },

  // Membros Inferiores
  coxa_direita: { x: 42, y: 90 },
  coxa_esquerda: { x: 58, y: 90 },
  joelho_direito: { x: 43, y: 115 },
  joelho_esquerdo: { x: 57, y: 115 },
  perna_direita: { x: 44, y: 145 },
  perna_esquerda: { x: 56, y: 145 },
  tornozelo_direito: { x: 44, y: 190 },
  tornozelo_esquerdo: { x: 56, y: 190 },
  pe_direito: { x: 44, y: 210 },
  pe_esquerdo: { x: 56, y: 210 },

  // Sistêmicos
  sistemico: { x: 5, y: 40 },
  mental: { x: 50, y: 2 },
  metabolico: { x: 95, y: 50 },
  sono: { x: 5, y: 15 },
};

const SEVERITY_COLORS: Record<number, string> = {
  1: "#22c55e", // Verde - Leve
  2: "#84cc16", // Lima - Leve-Moderado
  3: "#eab308", // Amarelo - Moderado
  4: "#f97316", // Laranja - Moderado-Severo
  5: "#ef4444", // Vermelho - Severo
};

const REGION_LABELS: Partial<Record<BodyRegion, string>> = {
  cabeca: "Cabeça",
  face: "Face",
  olhos: "Olhos",
  ouvidos: "Ouvidos",
  boca: "Boca",
  pescoco: "Pescoço",
  cervical: "Cervical",
  ombro_direito: "Ombro Direito",
  ombro_esquerdo: "Ombro Esquerdo",
  torax: "Tórax",
  costas_superior: "Costas Superior",
  braco_direito: "Braço Direito",
  braco_esquerdo: "Braço Esquerdo",
  cotovelo_direito: "Cotovelo Direito",
  cotovelo_esquerdo: "Cotovelo Esquerdo",
  antebraco_direito: "Antebraço Direito",
  antebraco_esquerdo: "Antebraço Esquerdo",
  punho_direito: "Punho Direito",
  punho_esquerdo: "Punho Esquerdo",
  mao_direita: "Mão Direita",
  mao_esquerda: "Mão Esquerda",
  toracica: "Torácica",
  abdominal: "Abdominal",
  lombar: "Lombar",
  sacral: "Sacral",
  quadril_direito: "Quadril Direito",
  quadril_esquerdo: "Quadril Esquerdo",
  coxa_direita: "Coxa Direita",
  coxa_esquerda: "Coxa Esquerda",
  joelho_direito: "Joelho Direito",
  joelho_esquerdo: "Joelho Esquerdo",
  perna_direita: "Perna Direita",
  perna_esquerda: "Perna Esquerda",
  tornozelo_direito: "Tornozelo Direito",
  tornozelo_esquerdo: "Tornozelo Esquerdo",
  pe_direito: "Pé Direito",
  pe_esquerdo: "Pé Esquerdo",
  sistemico: "Sistêmico",
  mental: "Mental",
  metabolico: "Metabólico",
  sono: "Sono",
};

function getRegionLabel(region: BodyRegion): string {
  return REGION_LABELS[region] || region.replace(/_/g, " ");
}

function getMarkerOffset(indexInRegion: number, totalInRegion: number): { x: number; y: number } {
  if (totalInRegion <= 1) {
    return { x: 0, y: 0 };
  }

  const markersPerRing = 6;
  const ring = Math.floor(indexInRegion / markersPerRing);
  const positionInRing = indexInRegion % markersPerRing;
  const markersInThisRing = Math.min(
    markersPerRing,
    totalInRegion - ring * markersPerRing
  );
  const radius = 4 + ring * 3.2;
  const angle = (2 * Math.PI * positionInRing) / Math.max(markersInThisRing, 1) - Math.PI / 2;

  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
  };
}

export function Vision360BodyMap2D({ markers, sex, onMarkerClick }: Vision360BodyMap2DProps) {
  const [searchParams] = useSearchParams();
  const calibrationMode = searchParams.get("calibrar") === "1";
  const [lastClick, setLastClick] = useState<{ x: number; y: number } | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<BodyRegion | null>(null);
  const [editedCoords, setEditedCoords] = useState<Record<BodyRegion, { x: number; y: number }> | null>(null);
  const [draggingRegion, setDraggingRegion] = useState<BodyRegion | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  
  const isFemale = sex === "F";
  const bodyImage = isFemale ? bodyMapFemale : bodyMapMale;
  const baseRegionCoords = isFemale ? REGION_COORDS_FEMALE : REGION_COORDS_MALE;
  const regionCoords = editedCoords || baseRegionCoords;
  const markersByRegion = markers.reduce<Record<string, number[]>>((acc, marker, index) => {
    if (!acc[marker.region]) {
      acc[marker.region] = [];
    }
    acc[marker.region].push(index);
    return acc;
  }, {});
  const sortedMarkers = [...markers].sort((a, b) => {
    if (b.severity !== a.severity) return b.severity - a.severity;
    return a.condition.localeCompare(b.condition, "pt-BR");
  });

  // Converter coordenadas de tela para SVG
  const screenToSvgCoords = (e: React.MouseEvent<SVGSVGElement> | MouseEvent) => {
    const svg = e.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 240;
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
  };

  // Handler para clique no modo calibração
  const handleCalibrationClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!calibrationMode || draggingRegion) return;
    
    const coords = screenToSvgCoords(e);
    setLastClick(coords);
    
    if (selectedRegion) {
      // Atualizar coordenadas da região selecionada
      const updated = { ...(editedCoords || baseRegionCoords) };
      updated[selectedRegion] = coords;
      setEditedCoords(updated);
      console.log(`📍 ${selectedRegion}: { x: ${coords.x}, y: ${coords.y} }`);
    } else {
      console.log(`📍 Clique: { x: ${coords.x}, y: ${coords.y} }`);
    }
  };

  // Handler para iniciar arrasto de um ponto
  const handlePointMouseDown = (e: React.MouseEvent<SVGCircleElement>, region: BodyRegion) => {
    if (!calibrationMode) return;
    e.stopPropagation();
    setDraggingRegion(region);
    setSelectedRegion(region);
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 240;
    const coords = { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
    setDragStart(coords);
  };

  // Handler para arrastar
  const handleMouseMove = (e: MouseEvent) => {
    if (!calibrationMode || !draggingRegion) return;
    
    const svg = document.querySelector('svg[viewBox="0 0 100 240"]');
    if (!svg) return;
    
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 240;
    const coords = { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
    
    const updated = { ...(editedCoords || baseRegionCoords) };
    updated[draggingRegion] = coords;
    setEditedCoords(updated);
    setLastClick(coords);
  };

  // Handler para soltar
  const handleMouseUp = () => {
    if (draggingRegion) {
      console.log(`📍 ${draggingRegion} atualizado: { x: ${regionCoords[draggingRegion].x}, y: ${regionCoords[draggingRegion].y} }`);
    }
    setDraggingRegion(null);
    setDragStart(null);
  };

  // Adicionar listeners de mouse global quando arrastando
  React.useEffect(() => {
    if (draggingRegion) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingRegion]);

  const copyCoordinatesToClipboard = () => {
    const coordsToCopy = editedCoords || baseRegionCoords;
    const genderPrefix = isFemale ? "REGION_COORDS_FEMALE" : "REGION_COORDS_MALE";
    const jsonString = JSON.stringify(coordsToCopy, null, 2);
    const code = `const ${genderPrefix}: Record<BodyRegion, { x: number; y: number }> = ${jsonString};`;
    
    navigator.clipboard.writeText(code).then(() => {
      alert("✅ Coordenadas copiadas para a área de transferência!");
    });
  };

  return (
    <div className="relative w-full flex items-start lg:items-stretch justify-start py-4 gap-3 flex-wrap lg:flex-nowrap px-2 min-w-0">
      {/* Container do mapa */}
      <div className="relative flex-shrink-0 order-1">
        {/* Container com aspect ratio fixo baseado na imagem */}
        <div className="relative w-full max-w-[220px]" style={{ aspectRatio: "800 / 1920" }}>
          {/* Imagem do corpo */}
          <motion.img
            src={bodyImage}
            alt={isFemale ? "Mapa corporal feminino" : "Mapa corporal masculino"}
            className="w-full h-full object-contain"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            draggable={false}
          />

          {/* Overlay SVG para marcadores */}
          <svg
            viewBox="0 0 100 240"
            className="absolute inset-0 w-full h-full"
            preserveAspectRatio="xMidYMid meet"
            onClick={handleCalibrationClick}
            style={{ cursor: calibrationMode && !draggingRegion ? "crosshair" : "default" }}
          >
          <defs>
            {/* Glow para marcadores */}
            <filter id="markerGlow">
              <feGaussianBlur stdDeviation="1" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Grid de referência no modo calibração */}
          {calibrationMode && (
            <>
              {/* Linhas horizontais a cada 20 unidades */}
              {[0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200, 220, 240].map(y => (
                <g key={`h-${y}`}>
                  <line x1="0" y1={y} x2="100" y2={y} stroke="red" strokeWidth="0.3" opacity="0.4" />
                  <text x="1" y={y + 2} fontSize="3" fill="red" opacity="0.7">{y}</text>
                </g>
              ))}
              {/* Linhas verticais a cada 10 unidades */}
              {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(x => (
                <g key={`v-${x}`}>
                  <line x1={x} y1="0" x2={x} y2="240" stroke="blue" strokeWidth="0.3" opacity="0.4" />
                  <text x={x + 0.5} y="5" fontSize="3" fill="blue" opacity="0.7">{x}</text>
                </g>
              ))}
              {/* Mostrar todos os pontos mapeados (exceto sistemico e sono) */}
              {Object.entries(regionCoords)
                .filter(([region]) => region !== "sistemico" && region !== "sono")
                .map(([region, coords]) => {
                const isSelected = selectedRegion === region;
                const edited = editedCoords?.[region as BodyRegion];
                const base = baseRegionCoords[region as BodyRegion];
                const isModified = editedCoords && edited && 
                                  (edited.x !== base.x || edited.y !== base.y);
                
                return (
                  <g key={region}>
                    {/* Linha conectando ao ponto original se modificado */}
                    {isModified && (
                      <line
                        x1={baseRegionCoords[region as BodyRegion].x}
                        y1={baseRegionCoords[region as BodyRegion].y}
                        x2={coords.x}
                        y2={coords.y}
                        stroke="yellow"
                        strokeWidth="0.3"
                        strokeDasharray="1,1"
                        opacity="0.5"
                      />
                    )}
                    {/* Círculo do ponto - arrastável */}
                    <circle 
                      cx={coords.x} 
                      cy={coords.y} 
                      r={isSelected ? "3" : "2"} 
                      fill={isSelected ? "yellow" : isModified ? "orange" : "magenta"} 
                      opacity={isSelected ? "1" : "0.6"}
                      stroke={isSelected ? "white" : "none"}
                      strokeWidth={isSelected ? "0.8" : "0"}
                      style={{ 
                        cursor: calibrationMode ? (isSelected ? "grab" : "pointer") : "default",
                        pointerEvents: calibrationMode ? "all" : "none"
                      }}
                      onMouseDown={(e) => {
                        if (calibrationMode) {
                          handlePointMouseDown(e, region as BodyRegion);
                        }
                      }}
                    />
                    {/* Label */}
                    <text 
                      x={coords.x + (isSelected ? 3 : 2)} 
                      y={coords.y + (isSelected ? 2 : 1)} 
                      fontSize={isSelected ? "3" : "2.5"} 
                      fill={isSelected ? "yellow" : isModified ? "orange" : "magenta"}
                      opacity={isSelected ? "1" : "0.8"}
                      fontWeight={isSelected ? "bold" : "normal"}
                    >
                      {region.replace(/_/g, " ")}
                    </text>
                    {/* Coordenadas ao lado */}
                    {isSelected && (
                      <text 
                        x={coords.x + 3} 
                        y={coords.y + 4} 
                        fontSize="2" 
                        fill="white"
                        opacity="0.9"
                      >
                        {`(${coords.x}, ${coords.y})`}
                      </text>
                    )}
                  </g>
                );
              })}
            </>
          )}

          {/* Marcadores de condições */}
          {markers.map((marker, index) => {
            const coords = regionCoords[marker.region];
            if (!coords) return null;

            const markerIndexes = markersByRegion[marker.region] || [];
            const indexInRegion = markerIndexes.indexOf(index);
            const offset = getMarkerOffset(indexInRegion, markerIndexes.length);
            const color = SEVERITY_COLORS[marker.severity] || SEVERITY_COLORS[3];
            const size = 2.5 + marker.severity * 0.4;
            const markerX = Math.max(3, Math.min(97, coords.x + offset.x));
            const markerY = Math.max(3, Math.min(237, coords.y + offset.y));

            return (
              <motion.g
                key={`${marker.id}-${index}`}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 + index * 0.05, duration: 0.3, type: "spring" }}
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkerClick?.(marker);
                }}
              >
                {/* Halo externo pulsante */}
                <motion.circle
                  cx={markerX}
                  cy={markerY}
                  r={size + 1.5}
                  fill="none"
                  stroke={color}
                  strokeWidth="0.4"
                  opacity="0.4"
                  animate={{
                    r: [size + 1.5, size + 3, size + 1.5],
                    opacity: [0.4, 0.15, 0.4],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                {/* Círculo do marcador */}
                <circle
                  cx={markerX}
                  cy={markerY}
                  r={size}
                  fill={color}
                  stroke="white"
                  strokeWidth="0.6"
                  filter="url(#markerGlow)"
                />

                {/* Indicador de severidade no centro */}
                <text
                  x={markerX}
                  y={markerY}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="white"
                  fontSize="2.5"
                  fontWeight="bold"
                >
                  {marker.severity}
                </text>
              </motion.g>
            );
          })}
        </svg>
        </div>

        {/* Legenda de severidade */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-2 text-xs text-muted-foreground">
          {[1, 2, 3, 4, 5].map((sev) => (
            <div key={sev} className="flex items-center gap-1">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: SEVERITY_COLORS[sev] }}
              />
              <span>{sev}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Lista de marcadores para melhor visualização */}
      {!calibrationMode && markers.length > 0 && (
        <div className="w-full lg:flex-1 lg:min-w-0 lg:max-w-full bg-muted/20 border rounded-lg p-3 h-auto lg:max-h-[560px] overflow-y-auto flex-shrink min-w-0 order-2">
          <p className="text-xs font-semibold text-muted-foreground mb-2">
            Marcadores ({markers.length})
          </p>
          <div className="space-y-2">
            {sortedMarkers.map((marker, idx) => (
              <button
                key={`marker-list-${marker.id}-${idx}`}
                type="button"
                onClick={() => onMarkerClick?.(marker)}
                className="w-full text-left p-2 rounded-md border bg-background/70 hover:bg-background transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium truncate">{marker.condition}</span>
                  <span
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
                    style={{ backgroundColor: SEVERITY_COLORS[marker.severity] || SEVERITY_COLORS[3] }}
                    title={`Severidade ${marker.severity}`}
                  >
                    {marker.severity}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {getRegionLabel(marker.region)}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Painel de calibração melhorado - agora à direita */}
      {calibrationMode && (
        <div className="bg-black/95 text-white text-xs p-4 rounded-lg w-[280px] max-h-[90vh] overflow-y-auto shadow-2xl flex-shrink-0 order-3 sticky top-4">
          <p className="font-bold mb-3 text-yellow-400 text-sm">🔧 Modo Calibração</p>
          
          {/* Instruções */}
          <div className="mb-3 space-y-1 text-[10px] text-gray-300">
            <p>1. Selecione uma região abaixo</p>
            <p>2. Arraste o ponto ou clique no corpo</p>
            <p>3. Copie o código atualizado</p>
          </div>

          {/* Região selecionada */}
          {selectedRegion && (
            <div className="mb-3 p-2 bg-yellow-500/20 rounded border border-yellow-500/50">
              <p className="text-[10px] text-yellow-400 mb-1">Região selecionada:</p>
              <p className="font-mono text-xs font-bold">{selectedRegion}</p>
              {regionCoords[selectedRegion] && (
                <p className="text-[10px] mt-1 text-gray-300">
                  {`x: ${regionCoords[selectedRegion].x}, y: ${regionCoords[selectedRegion].y}`}
                </p>
              )}
            </div>
          )}

          {/* Último clique */}
          {lastClick && (
            <div className="mb-3 p-2 bg-blue-500/20 rounded border border-blue-500/50">
              <p className="text-[10px] text-blue-400 mb-1">Último clique:</p>
              <p className="font-mono text-xs">
                {`{ x: ${lastClick.x}, y: ${lastClick.y} }`}
              </p>
            </div>
          )}

          {/* Lista de regiões para selecionar */}
          <div className="mb-3">
            <p className="text-[10px] text-gray-400 mb-2">Selecionar região:</p>
            <div className="grid grid-cols-2 gap-1 max-h-[200px] overflow-y-auto">
              {(Object.keys(regionCoords) as BodyRegion[])
                .filter((region) => region !== "sistemico" && region !== "sono")
                .map((region) => (
                <button
                  key={region}
                  onClick={() => setSelectedRegion(region)}
                  className={`text-[9px] p-1 rounded text-left transition-colors ${
                    selectedRegion === region
                      ? "bg-yellow-500 text-black font-bold"
                      : "bg-gray-800 hover:bg-gray-700"
                  }`}
                  title={`${region}: x: ${regionCoords[region].x}, y: ${regionCoords[region].y}`}
                >
                  {region.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>

          {/* Informações */}
          <div className="mb-3 text-[10px] text-gray-400">
            <p>Sexo: {isFemale ? "Feminino" : "Masculino"}</p>
            <p>ViewBox: 0 0 100 240</p>
            {editedCoords && (
              <p className="text-yellow-400 mt-1">⚠️ Coordenadas modificadas</p>
            )}
          </div>

          {/* Botões de ação */}
          <div className="space-y-2">
            <button
              onClick={copyCoordinatesToClipboard}
              className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-xs font-semibold"
            >
              📋 Copiar Código
            </button>
            <button
              onClick={() => {
                setEditedCoords(null);
                setSelectedRegion(null);
                setLastClick(null);
              }}
              className="w-full bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded text-xs"
            >
              🔄 Resetar
            </button>
            <button
              onClick={() => console.log("📋 Coordenadas:", JSON.stringify(regionCoords, null, 2))}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-xs"
            >
              📊 Log no Console
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
